/**
 * Fertilizer Rate Calculator engine — pure functions, no side effects.
 *
 * Converts a bag analysis (N-P-K), a target nitrogen rate, and a lawn size
 * into an application rate: pounds of product per 1,000 sq ft, total pounds
 * for the lawn, bags to buy, and the actual N-P-K delivered per 1,000 sq ft.
 */

import { wholeContainers } from '@/lib/units/whole-containers'

export interface FertilizerRateInput {
  /** Nitrogen percent from the bag (first number of N-P-K) */
  n: number
  /** Phosphorus percent from the bag */
  p: number
  /** Potassium percent from the bag */
  k: number
  /** Target lb of nitrogen per 1,000 sq ft (0.5 light – 1.5 heavy) */
  targetN: number
  /** Lawn size in square feet */
  areaSqFt: number
  /** Bag weight used for the bags-to-buy count */
  bagLbs?: number
  /** Optional price per bag in dollars; enables the cost outputs */
  bagPrice?: number
}

export interface FertilizerRateResult {
  /** lbs of product per 1,000 sq ft */
  per1000: number
  per1000Label: string
  /** total lbs of product for the whole lawn */
  totalLbs: number
  totalLbsLabel: string
  /** whole bags to buy (never 0) */
  bags: number
  bagLbs: number
  /** actual nutrient lbs delivered per 1,000 sq ft at this rate */
  nDelivered: number
  pDelivered: number
  kDelivered: number
  nDeliveredLabel: string
  pDeliveredLabel: string
  kDeliveredLabel: string
  npkLabel: string
  areaLabel: string
  targetLabel: string
  /** Cost outputs — null unless a bag price was provided */
  costPerApp: number | null
  costPerAppLabel: string | null
  /** Price per lb of actual nitrogen in the bag — the comparison-shopping number */
  costPerLbN: number | null
  costPerLbNLabel: string | null
}

export const BAG_SIZE_PRESETS = [15, 25, 40, 50] as const

export const FERTILIZER_PRESETS = [
  { label: '10-10-10', n: 10, p: 10, k: 10 },
  { label: '13-13-13', n: 13, p: 13, k: 13 },
  { label: '19-19-19', n: 19, p: 19, k: 19 },
  { label: '16-4-8', n: 16, p: 4, k: 8 },
  { label: '32-0-4', n: 32, p: 0, k: 4 },
  { label: '46-0-0', n: 46, p: 0, k: 0 },
] as const

const DEFAULT_BAG_LBS = 50
export function calculateFertilizerRate(input: FertilizerRateInput): FertilizerRateResult {
  const n = Number.isFinite(input.n) ? Math.max(input.n, 0) : 0
  const p = Number.isFinite(input.p) ? Math.max(input.p, 0) : 0
  const k = Number.isFinite(input.k) ? Math.max(input.k, 0) : 0
  const areaSqFt = Number.isFinite(input.areaSqFt) ? Math.max(input.areaSqFt, 0) : 0
  const targetN = Number.isFinite(input.targetN) ? Math.min(Math.max(input.targetN, 0.25), 2) : 1
  const bagLbs = input.bagLbs && input.bagLbs > 0 ? input.bagLbs : DEFAULT_BAG_LBS

  // A 0-N product cannot be converted from a nitrogen target. Return a safe
  // zero result and let the UI direct the user to the product's label rate.
  const per1000 = n > 0 ? targetN / (n / 100) : 0
  const totalLbs = per1000 * (areaSqFt / 1000)
  const bags = totalLbs > 0 ? wholeContainers(totalLbs, bagLbs) : 0

  const nDelivered = per1000 * (n / 100)
  const pDelivered = per1000 * (p / 100)
  const kDelivered = per1000 * (k / 100)

  // Cost math (advanced): cost of the product actually spread this application,
  // and the price per lb of actual nitrogen — the number that makes a $60
  // high-analysis bag cheaper than a $25 low-analysis one.
  const bagPrice = input.bagPrice && input.bagPrice > 0 ? input.bagPrice : null
  const costPerApp = bagPrice !== null ? (totalLbs / bagLbs) * bagPrice : null
  const lbsNPerBag = bagLbs * (n / 100)
  const costPerLbN = bagPrice !== null && lbsNPerBag > 0 ? bagPrice / lbsNPerBag : null

  return {
    per1000,
    per1000Label: per1000.toFixed(1),
    totalLbs,
    totalLbsLabel: totalLbs.toFixed(0),
    bags,
    bagLbs,
    nDelivered,
    pDelivered,
    kDelivered,
    nDeliveredLabel: nDelivered.toFixed(2),
    pDeliveredLabel: pDelivered.toFixed(2),
    kDeliveredLabel: kDelivered.toFixed(2),
    npkLabel: `${n}-${p}-${k}`,
    areaLabel: areaSqFt.toLocaleString('en-US'),
    targetLabel: targetN.toFixed(2).replace(/0$/, '').replace(/\.$/, '.0'),
    costPerApp,
    costPerAppLabel: costPerApp !== null ? `$${costPerApp.toFixed(2)}` : null,
    costPerLbN,
    costPerLbNLabel: costPerLbN !== null ? `$${costPerLbN.toFixed(2)}` : null,
  }
}
